import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import { ArrowRight, LogIn } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === "admin" ? "/app/admin" : "/app/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative border-r border-[var(--border)] grid-lines">
        <div className="h-full flex items-center justify-center p-12">
          <div>
            <Link to="/" className="mb-10 inline-block" data-testid="login-logo">
              <Brand size="lg" />
            </Link>
            <h1 className="font-display text-5xl leading-[0.95] tracking-tight">
              Back on the
              <br />
              <span className="text-[var(--primary)]">tools.</span>
            </h1>
            <p className="text-zinc-400 mt-6 max-w-sm">
              Sign in to pick up where you left off — quotes, invoices and
              mileage, all in one workshop.
            </p>
            <div className="hi-vis-stripe h-1.5 mt-10 w-32" />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          className="w-full max-w-sm card-sharp p-8"
          data-testid="login-form"
        >
          <div className="label-mono">/ LOGIN</div>
          <h2 className="font-display text-3xl mt-2">Welcome back</h2>
          <div className="mt-6 space-y-4">
            <div>
              <div className="label-mono mb-2">Email</div>
              <input
                required
                type="email"
                className="input-sharp"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="you@email.co.nz"
              />
            </div>
            <div>
              <div className="label-mono mb-2">Password</div>
              <input
                required
                type="password"
                className="input-sharp"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div
                className="text-sm font-mono text-[var(--error)] border border-[var(--error)] px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              className="btn-primary w-full justify-center"
              disabled={submitting}
              data-testid="login-submit-btn"
            >
              <LogIn size={14} /> {submitting ? "Signing in…" : "Sign in"}
            </button>
          </div>
          <div className="mt-6 text-center text-xs font-mono text-zinc-500">
            No account yet?{" "}
            <Link to="/register" className="text-[var(--primary)]" data-testid="login-to-register">
              Register <ArrowRight size={12} className="inline" />
            </Link>
          </div>
          <div className="mt-2 text-center text-xs font-mono text-zinc-500">
            Or try{" "}
            <Link to="/tools/gst" className="text-[var(--primary)]" data-testid="login-to-guest">
              guest mode
            </Link>
          </div>
          <div className="mt-6 pt-6 border-t border-[var(--border)] text-center text-[10px] font-mono text-zinc-500 uppercase tracking-wider leading-relaxed">
            Built by IRD-registered tax agents at NZ Taxation Ltd · Te Atatū, Auckland
          </div>
        </form>
      </div>
    </div>
  );
}
